import { FC, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { budgetDetailsPeriodSelector } from "@/store/budgets/selectors/details-drawer/budgetDetailsPeriodSelector";
import { budgetDetailsAccumulatedCostDateOptionsSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostDateOptionsSelector";
import { BudgetPeriod } from "@/services/cloudchipr.api";
import { budgetDetailsDrawerAccumulatedCostDateSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsDrawerAccumulatedCostDateSelector";
import { setBudgetDetailsDrawerAccumulatedCostDate } from "@/store/budgets/budgetsSlice";
import { getBudgetDetailsAccumulatedCostByBudgetIdAndDateThunk } from "@/store/budgets/thunks/drawer/getBudgetDetailsAccumulatedCostByBudgetIdAndDateThunk";
import { budgetDetailsByIdLoadingSelector } from "@/store/budgets/selectors/details-drawer/budgetDetailsByIdLoadingSelector";
import { budgetDetailsAccumulatedCostLoadingSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostLoadingSelector";

export const BudgetDetailsAccumulatedCostDateSelect: FC = () => {
  const dispatch = useAppDispatch();
  const period = useAppSelector(budgetDetailsPeriodSelector);
  const options = useAppSelector(
    budgetDetailsAccumulatedCostDateOptionsSelector,
  );
  const date = useAppSelector(budgetDetailsDrawerAccumulatedCostDateSelector);

  const detailsLoading = useAppSelector(budgetDetailsByIdLoadingSelector);
  const accumulatedCostLoading = useAppSelector(
    budgetDetailsAccumulatedCostLoadingSelector,
  );
  const loading = detailsLoading || accumulatedCostLoading;

  const label = period ? (labelByPeriod[period] ?? "Date") : "Date";

  const changeHandler = useCallback(
    (event: SelectChangeEvent) => {
      const newDate = event.target.value;

      dispatch(setBudgetDetailsDrawerAccumulatedCostDate(newDate));
      dispatch(getBudgetDetailsAccumulatedCostByBudgetIdAndDateThunk());
    },
    [dispatch],
  );

  if (period === "custom" || !options.length || options.length === 1) {
    return null;
  }

  return (
    <FormControl size="xsmall" disabled={loading} sx={{ minWidth: 160 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        fullWidth
        size="small"
        value={date}
        label={label}
        disabled={loading}
        onChange={changeHandler}
      >
        {options.map(({ value, title }) => (
          <MenuItem key={value} value={value}>
            <Typography variant="caption">{title}</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const labelByPeriod: Partial<Record<BudgetPeriod, string>> = {
  monthly: "Month",
  quarterly: "Quarter",
  annually: "Year",
};
